import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { IconButton } from "@mui/material";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import {
  fetchBPDashboardData,
  fetchDashboardCountryPreferences,
  updateDashboardCountryPreferences,
} from "../../store/slices/dashboard";
import {
  EXTENSION_REQUEST_COLUMNS,
  PENDING_ERRORS_COLUMNS,
  PENDING_BP_CHECK_COLUMNS,
  DASHBOARD_TITLES,
} from "../../constants/dashboardConstants";
import { 
  COUNTRY_CODE_MAP, 
  ROUTE_EXTENSION_BP, 
  ROUTE_EXTENSION_ERRORS,
  ROUTE_EXTENSION_JDF,
  ROUTE_IB_CACLC 
} from "../../constants/constant";
import DashboardCard from "./DashboardCard";
import NotificationBanner from "./NotificationBanner";
import DashboardConfigModal from "./DashboardConfigModal";
import PageLoader from "../../common/PageLoader";
import ToastMessageFunction from "../../common/toast-message-function";
import styles from "./BPDashboard.module.css";

const BPDashboard = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const toastManager = ToastMessageFunction();
  const [isConfigModalOpen, setIsConfigModalOpen] = useState(false);
  const [selectedCountryNames, setSelectedCountryNames] = useState([]);
  const [isLoadingPreferences, setIsLoadingPreferences] = useState(true);

  const { extensionRequests, pendingErrors, pendingBPCheck, loading, error } = useSelector(
    (state) => state.bpDashboard
  );

  useEffect(() => {
    const loadDashboardData = async () => {
      try {
        setIsLoadingPreferences(true);
        
       
        dispatch(fetchBPDashboardData());
        const preferredCountries = await dispatch(fetchDashboardCountryPreferences());
        
        if (preferredCountries && preferredCountries.length > 0) {
          const countryNames = preferredCountries.map((code) => COUNTRY_CODE_MAP[code] || code);
          setSelectedCountryNames(countryNames);
        } else {
          setSelectedCountryNames([]);
        }
      } catch (error) {
        console.error("Error loading dashboard data:", error);
        toastManager.getToast("Failed to load dashboard preferences", "error");
        setSelectedCountryNames([]);
      } finally {
        setIsLoadingPreferences(false);
      }
    };

    loadDashboardData();
  }, [dispatch]);

  const handleOpenConfigModal = () => {
    setIsConfigModalOpen(true);
  };

  const handleCloseConfigModal = () => {
    setIsConfigModalOpen(false);
  };

  const handleConfirmCountries = async (countryCodes) => {
    try {
      await dispatch(updateDashboardCountryPreferences(countryCodes));
      const countryNames = countryCodes.map((code) => COUNTRY_CODE_MAP[code] || code);
      setSelectedCountryNames(countryNames);
      dispatch(fetchBPDashboardData(countryCodes));
      
      toastManager.getToast("Dashboard preferences updated successfully", "success");
    } catch (error) {
      console.error("Error updating dashboard preferences:", error);
      toastManager.getToast("Failed to update dashboard preferences", "error");
    }
  };

  const handleExtensionRequestClick = (row, field) => {
    const countryCode = row.country;
    const countryName = COUNTRY_CODE_MAP[countryCode] || countryCode;
    const pathname = field === 'bp' ? ROUTE_EXTENSION_BP : ROUTE_EXTENSION_JDF;
    history.push({
      pathname,
      state: { country: countryCode, countryCode, countryName }
    });
  };

  const handlePendingErrorsClick = (row, field) => {
    const countryCode = row.country;
    const countryName = COUNTRY_CODE_MAP[countryCode] || countryCode;
    if (field === 'errors') {
      history.push({
        pathname: ROUTE_EXTENSION_ERRORS,
        state: { country: countryCode, countryCode, countryName }
      });
    }
  };

  const handlePendingBPCheckClick = (row, field) => {
    const countryCode = row.country;
    const countryName = COUNTRY_CODE_MAP[countryCode] || countryCode;
    history.push({
      pathname: ROUTE_IB_CACLC,
      state: { country: countryCode, countryCode, countryName }
    });
  };

  return (
    <div className={styles.dashboardContainer}>
      {(loading || isLoadingPreferences) && <PageLoader />}
      <div className={styles.dashboardHeader}>
        <h1 className={styles.dashboardTitle}>Dashboard</h1>
        <IconButton className={styles.settingsIcon} onClick={handleOpenConfigModal}>
          <SettingsOutlinedIcon />
        </IconButton>
      </div>

      <NotificationBanner />

      <div className={styles.sectionContainer}>
        <h2 className={styles.sectionTitle}>
          {DASHBOARD_TITLES.EXTENSION_APPLICATION}
        </h2>

        <div className={styles.gridContainer}>
          {/* Pending Extension Requests */}
          <DashboardCard
            title={DASHBOARD_TITLES.PENDING_EXTENSION_REQUESTS}
            subtitle="⏱ Overdue and due next working day"
            columns={EXTENSION_REQUEST_COLUMNS}
            rows={extensionRequests}
            loading={false}
            error={error}
            onCellClick={handleExtensionRequestClick}
          />

          {/* Pending Errors */}
          <DashboardCard
            title={DASHBOARD_TITLES.PENDING_ERRORS}
            columns={PENDING_ERRORS_COLUMNS}
            rows={pendingErrors}
            loading={false}
            error={error}
            hideHeader={true}
            titleBottomSpace={pendingErrors?.length > 0 ? 55 : 0}
            onCellClick={handlePendingErrorsClick}
          />
        </div>
      </div>
      <div className={styles.sectionContainer}>
        <h2 className={styles.sectionTitle}>
          {DASHBOARD_TITLES.INTEREST_BEARING}
        </h2>

        <div className={styles.gridContainer}>
          {/* Pending BP Check */}
          <DashboardCard
            title={DASHBOARD_TITLES.PENDING_BP_CHECK}
            columns={PENDING_BP_CHECK_COLUMNS}
            rows={pendingBPCheck}
            loading={false}
            error={error}
            hideHeader={true}
            onCellClick={handlePendingBPCheckClick}
          />
          <div></div>
        </div>
      </div>
      <DashboardConfigModal
        isOpen={isConfigModalOpen}
        onClose={handleCloseConfigModal}
        onConfirm={handleConfirmCountries}
        initialCountries={selectedCountryNames}
      />
      {toastManager.toast}
    </div>
  );
};

export default BPDashboard;
